import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#b45309", amberBg: "#fffbeb", amberBorder: "#fde68a",
  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:    <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:   <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    Search:       <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
    MapPin:       <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Star:         <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    CheckCircle:  <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    BadgeCheck:   <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
    Shield:       <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    ShieldCheck:  <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    Calendar:     <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    Clock:        <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Wallet:       <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/></>,
    Zap:          <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Users:        <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    ChevronDown:  <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:    <><path d="m18 15-6-6-6 6"/></>,
    Dumbbell:     <><path d="M14.4 14.4 9.6 9.6"/><path d="M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.767 1.768a2 2 0 1 1-2.829-2.829l6.364-6.364a2 2 0 1 1 2.829 2.829l-1.768 1.767a2 2 0 1 1 2.828 2.829z"/><path d="m21.5 21.5-1.4-1.4"/><path d="M3.9 3.9 2.5 2.5"/><path d="M6.404 12.768a2 2 0 1 1-2.829-2.829l1.768-1.767a2 2 0 1 1-2.828-2.829l2.828-2.828a2 2 0 1 1 2.829 2.828l1.767-1.768a2 2 0 1 1 2.829 2.829z"/></>,
    Heart:        <><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></>,
    TrendingUp:   <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
    Home:         <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    Leaf:         <><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></>,
    Award:        <><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></>,
    Target:       <><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>,
    Bike:         <><circle cx="18.5" cy="17.5" r="3.5"/><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="15" cy="5" r="1"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/></>,
    Waves:        <><path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/></>,
    Flame:        <><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></>,
    X:            <><path d="M18 6 6 18"/><path d="m6 6 12 12"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Location suggestions ── */
const LOCATIONS = [
  "Westlands, Nairobi", "Kilimani, Nairobi", "Karen, Nairobi",
  "Lavington, Nairobi", "Parklands, Nairobi", "South B, Nairobi",
  "Langata, Nairobi", "Kasarani, Nairobi", "Ruaka, Nairobi",
  "Nyali, Mombasa", "Bamburi, Mombasa", "Mombasa CBD",
  "Kisumu CBD", "Milimani, Kisumu",
  "Nakuru CBD", "Nakuru Town",
  "Eldoret CBD", "Thika",
];

/* ── Sub-services ── */
const SERVICES = [
  {
    icon: "Dumbbell",
    label: "Personal Training",
    sub: "One-on-one at home or outdoors",
    range: "KES 1,500 – 4,000",
    note: "per session",
    desc: "A dedicated trainer comes to your home, compound, or local park. Sessions are tailored to your goals -- weight loss, muscle building, general fitness, or injury recovery.",
  },
  {
    icon: "Leaf",
    label: "Yoga and Pilates",
    sub: "Private or small group",
    range: "KES 1,200 – 3,000",
    note: "per session",
    desc: "Private yoga or pilates instruction at home or in a space of your choice. Suitable for all levels from total beginners to advanced practitioners.",
  },
  {
    icon: "Heart",
    label: "Group Fitness Classes",
    sub: "HIIT, aerobics, Zumba and more",
    range: "KES 500 – 1,500",
    note: "per person",
    desc: "Join a scheduled group session at a local gym or community space. Classes include HIIT, aerobics, Zumba, and bootcamp formats. Ideal if you prefer a social training environment.",
  },
  {
    icon: "Target",
    label: "Strength and Conditioning",
    sub: "Results-focused programming",
    range: "KES 2,000 – 5,000",
    note: "per session",
    desc: "Structured strength and conditioning programming designed around your goals. Delivered by certified trainers with experience in periodisation and progressive overload.",
  },
  {
    icon: "Waves",
    label: "Swimming Lessons",
    sub: "Private pools and public facilities",
    range: "KES 1,500 – 3,500",
    note: "per session",
    desc: "Private swimming instruction for adults and children. Beginner, intermediate, and advanced levels. Trainers travel to private pool locations or meet you at a facility.",
  },
  {
    icon: "Bike",
    label: "Outdoor and Sports Coaching",
    sub: "Running, cycling, sports-specific",
    range: "KES 1,500 – 4,500",
    note: "per session",
    desc: "Running programmes, cycling coaching, sports conditioning for football, tennis, and other disciplines. Sessions happen outdoors in your neighbourhood or at a facility.",
  },
  {
    icon: "TrendingUp",
    label: "Weight Loss Programmes",
    sub: "Structured multi-week plans",
    range: "KES 8,000 – 25,000",
    note: "per month",
    desc: "A committed multi-week programme combining training sessions, check-ins, and accountability. Trainers set progressive goals and adjust the plan as you build fitness.",
  },
  {
    icon: "Flame",
    label: "Post-Natal and Pre-Natal Fitness",
    sub: "Safe, specialist-led sessions",
    range: "KES 1,500 – 3,500",
    note: "per session",
    desc: "Fitness sessions specifically designed for pregnancy and post-natal recovery. All trainers offering this service hold a recognised pre/post-natal fitness certification.",
  },
  {
    icon: "Award",
    label: "Nutrition and Wellness Coaching",
    sub: "Combine training with a nutrition plan",
    range: "KES 3,000 – 10,000",
    note: "per month",
    desc: "Some JiraniPro trainers offer combined fitness and nutrition coaching. Receive a structured eating plan alongside your training programme for more measurable results.",
  },
];

/* ── FAQ data ── */
const FAQS = [
  {
    q: "Do trainers bring their own equipment?",
    a: "It depends on the trainer and the service. Many personal trainers bring portable equipment -- resistance bands, kettlebells, mats, skipping ropes -- for home sessions. Some require access to a basic gym setup. Check the trainer's profile for what they include, and use in-app messaging to confirm before booking if you are unsure.",
  },
  {
    q: "How is a personal trainer different from a gym class?",
    a: "A personal training session is one-on-one and entirely focused on you -- your goals, your pace, your limitations. The trainer designs every session around your specific situation. Group classes are a lower cost per session and work well if you enjoy a social environment and the format suits your goals. Both are available through JiraniApp depending on what you are looking for.",
  },
  {
    q: "Can I book recurring sessions at the same time each week?",
    a: "Yes. Once you have had a first session with a trainer you want to keep working with, you can book recurring slots directly through the platform. You set the day, time, and frequency. Your trainer's availability calendar updates in real time so you always see accurate slots.",
  },
  {
    q: "What if I need to cancel a session?",
    a: "Cancellations made at least 24 hours before a session are free of charge. Late cancellations within 24 hours may incur a small cancellation fee, which is shown clearly when you cancel. You can reschedule from the Bookings section. Trainers can also cancel -- if they do within 24 hours of your session, you are not charged.",
  },
  {
    q: "Are JiraniApp trainers certified?",
    a: "All fitness professionals on JiraniApp go through our standard identity verification. For specialist services -- pre/post-natal fitness, injury rehabilitation, strength and conditioning -- we require proof of relevant certification before the trainer's profile goes live for that sub-service. You will see a Verified badge and any listed certifications on each trainer's profile.",
  },
  {
    q: "Can I try a trainer once before committing to a programme?",
    a: "Absolutely. Most clients book a single session first to assess fit before committing to a weekly schedule or a multi-week programme. There is no obligation to rebook after a single session. If the trainer is not the right fit, book a different one.",
  },
  {
    q: "I am a complete beginner. Will I be judged?",
    a: "No. Many clients on JiraniApp are starting from scratch. Good trainers are used to working with beginners and will meet you exactly where you are. When you book, you can note your experience level and goals in the booking form so the trainer comes prepared.",
  },
  {
    q: "What if the session did not meet what was agreed?",
    a: "Open a dispute through Help and Support within 48 hours of the session. Describe what was agreed and what was different. Our Trust and Safety team reviews every dispute and will work toward a fair resolution, including a partial or full refund where warranted.",
  },
];

/* ── Star rating ── */
function Stars({ count = 5 }) {
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Icon key={i} name="Star" size={13}
          color={i < count ? T.amber : T.n200}
          strokeWidth={i < count ? 0 : 1.5}
          style={{ fill: i < count ? T.amber : "none" }}
        />
      ))}
    </div>
  );
}

/* ── Location search ── */
function LocationSearch({ onSearch }) {
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (value.length < 2) { setSuggestions([]); return; }
    setSuggestions(LOCATIONS.filter(l => l.toLowerCase().includes(value.toLowerCase())).slice(0, 5));
  }, [value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setFocused(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (loc) => { setValue(loc); setSuggestions([]); setFocused(false); onSearch(loc); };
  const handleSubmit = () => { if (value.trim()) onSearch(value.trim()); };

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div style={{
        display: "flex", alignItems: "center",
        background: T.white, borderRadius: 14,
        border: `1.5px solid ${focused ? T.g400 : T.n200}`,
        boxShadow: focused ? `0 0 0 4px ${T.g100}` : "0 2px 12px rgba(0,0,0,.08)",
        overflow: "hidden", transition: "all .2s",
      }}>
        <span style={{ paddingLeft: 18, flexShrink: 0 }}>
          <Icon name="MapPin" size={18} color={focused ? T.g500 : T.n400} strokeWidth={1.8} />
        </span>
        <input
          value={value}
          onChange={e => setValue(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={e => e.key === "Enter" && handleSubmit()}
          placeholder="Enter your neighbourhood or city..."
          style={{
            flex: 1, padding: "16px 14px", border: "none", outline: "none",
            fontSize: 15, fontFamily: T.sans, color: T.n900, background: "transparent",
          }}
        />
        {value && (
          <button onClick={() => { setValue(""); setSuggestions([]); }}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "0 10px", color: T.n400, display: "flex" }}>
            <Icon name="X" size={15} strokeWidth={2} />
          </button>
        )}
        <button onClick={handleSubmit} style={{
          margin: 6, padding: "11px 22px", background: T.g500, color: T.white,
          border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600,
          fontFamily: T.sans, cursor: "pointer", display: "flex", alignItems: "center", gap: 7,
          flexShrink: 0, whiteSpace: "nowrap",
        }}
          onMouseEnter={e => e.currentTarget.style.background = T.g600}
          onMouseLeave={e => e.currentTarget.style.background = T.g500}>
          <Icon name="Search" size={15} color={T.white} strokeWidth={2} />
          Find trainers
        </button>
      </div>

      {/* Suggestions dropdown */}
      {focused && suggestions.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0,
          background: T.white, borderRadius: 12, border: `1px solid ${T.n150}`,
          boxShadow: "0 8px 32px rgba(0,0,0,.12)", zIndex: 100, overflow: "hidden",
        }}>
          {suggestions.map((s, i) => (
            <button key={i} onClick={() => handleSelect(s)} style={{
              width: "100%", textAlign: "left", padding: "13px 18px",
              background: "none", border: "none", cursor: "pointer",
              fontFamily: T.sans, fontSize: 14, color: T.n700,
              display: "flex", alignItems: "center", gap: 10,
              borderBottom: i < suggestions.length - 1 ? `1px solid ${T.n100}` : "none",
              transition: "background .1s",
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.n50}
              onMouseLeave={e => e.currentTarget.style.background = "none"}>
              <Icon name="MapPin" size={14} color={T.n400} strokeWidth={1.8} />
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Service card ── */
function ServiceCard({ icon, label, sub, range, note, desc }) {
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 16, border: `1.5px solid ${hovered ? T.g200 : T.n150}`,
        background: hovered ? T.g50 : T.white,
        transition: "all .18s", overflow: "hidden",
      }}>
      <div style={{ padding: "20px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: hovered ? T.g100 : T.n50, border: `1px solid ${hovered ? T.g200 : T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .18s", flexShrink: 0 }}>
            <Icon name={icon} size={18} color={hovered ? T.g600 : T.n400} strokeWidth={1.7} />
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: T.serif, fontSize: 18, color: hovered ? T.g700 : T.n900, letterSpacing: "-0.02em", lineHeight: 1.1 }}>{range}</div>
            <div style={{ fontSize: 11, color: T.n400, marginTop: 2 }}>{note}</div>
          </div>
        </div>
        <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 600, color: T.n900, marginBottom: 3, letterSpacing: "-0.01em" }}>{label}</div>
        <div style={{ fontSize: 12, color: T.n400, marginBottom: 10 }}>{sub}</div>
        <button onClick={() => setExpanded(o => !o)} style={{
          display: "flex", alignItems: "center", gap: 5,
          background: "none", border: "none", cursor: "pointer",
          fontFamily: T.sans, fontSize: 12, fontWeight: 600,
          color: expanded ? T.g600 : T.n400, padding: 0, transition: "color .15s",
        }}>
          {expanded ? "Less detail" : "What's included"}
          <Icon name={expanded ? "ChevronUp" : "ChevronDown"} size={13} strokeWidth={2.2} color={expanded ? T.g600 : T.n400} />
        </button>
      </div>
      <div style={{ overflow: "hidden", maxHeight: expanded ? 200 : 0, transition: "max-height .3s cubic-bezier(.4,0,.2,1)" }}>
        <div style={{ padding: "0 20px 20px" }}>
          <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.75, margin: 0 }}>{desc}</p>
        </div>
      </div>
    </div>
  );
}

/* ── Step ── */
function HowStep({ num, icon, title, desc, isLast }) {
  return (
    <div style={{ display: "flex", gap: 18, position: "relative" }}>
      {!isLast && (
        <div style={{ position: "absolute", left: 19, top: 44, bottom: -8, width: 1, background: `linear-gradient(to bottom, ${T.g200}, ${T.n150})` }} />
      )}
      <div style={{ width: 40, height: 40, borderRadius: "50%", background: T.g50, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1 }}>
        <Icon name={icon} size={16} color={T.g600} strokeWidth={1.8} />
      </div>
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 32 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.g500, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 4 }}>Step {num}</div>
        <div style={{ fontFamily: T.sans, fontSize: 15, fontWeight: 600, color: T.n900, marginBottom: 6, letterSpacing: "-0.01em" }}>{title}</div>
        <p style={{ fontFamily: T.sans, fontSize: 14, color: T.n600, lineHeight: 1.75, margin: 0 }}>{desc}</p>
      </div>
    </div>
  );
}

/* ── Testimonial ── */
function Testimonial({ name, area, service, stars, text }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("");
  const palette = [
    { bg: T.g50,     text: T.g700,   border: T.g100 },
    { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
    { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  ];
  const c = palette[name.charCodeAt(0) % palette.length];
  return (
    <div style={{ padding: "24px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white, display: "flex", flexDirection: "column", gap: 14 }}>
      <Stars count={stars} />
      <p style={{ fontFamily: T.sans, fontSize: 14, color: T.n600, lineHeight: 1.8, margin: 0, fontStyle: "italic" }}>"{text}"</p>
      <div style={{ borderTop: `1px solid ${T.n100}`, paddingTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: c.bg, border: `1.5px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: c.text, fontFamily: T.sans, flexShrink: 0 }}>{initials}</div>
        <div>
          <div style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.n900 }}>{name}</div>
          <div style={{ fontSize: 12, color: T.n400 }}>{service} · {area}</div>
        </div>
      </div>
    </div>
  );
}

/* ── FAQ item ── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "20px 0", background: "none", border: "none", cursor: "pointer",
        fontFamily: T.sans, textAlign: "left", gap: 16,
      }}>
        <span style={{ fontSize: 15, fontWeight: 400, color: T.n800, lineHeight: 1.45, letterSpacing: "-0.01em" }}>{q}</span>
        <span style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
          border: `1px solid ${open ? T.g500 : T.n200}`,
          background: open ? T.g500 : "transparent",
          color: open ? T.white : T.n400,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .22s", fontSize: 18, fontWeight: 300, lineHeight: 1,
          transform: open ? "rotate(45deg)" : "none",
        }}>+</span>
      </button>
      <div style={{ overflow: "hidden", maxHeight: open ? 400 : 0, transition: "max-height .32s cubic-bezier(.4,0,.2,1)" }}>
        <p style={{ fontFamily: T.sans, fontSize: 14, color: T.n600, lineHeight: 1.8, margin: "0 0 20px", paddingRight: 40 }}>{a}</p>
      </div>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function FitnessTrainingPage() {
  useFonts();
  const navigate = useNavigate();

  const handleSearch = (location) => {
    navigate(`/search?category=fitness&location=${encodeURIComponent(location)}`);
  };

  const TESTIMONIALS = [
    {
      name: "Cynthia A.", area: "Kilimani, Nairobi", service: "Personal Training", stars: 5,
      text: "I had tried gym memberships three times and never stuck with them. Having a trainer come to my apartment at 6:30am made all the difference. Eight months in, I have not missed a session. The accountability is real.",
    },
    {
      name: "Brian O.", area: "Westlands, Nairobi", service: "Strength and Conditioning", stars: 5,
      text: "My trainer built a full 12-week programme from scratch based on my goals. He tracks everything, adjusts when something is not working, and actually explains why he programmes the way he does. Genuinely excellent.",
    },
    {
      name: "Lina M.", area: "Nyali, Mombasa", service: "Yoga", stars: 5,
      text: "I have been doing private yoga sessions twice a week for four months. My instructor is patient, knowledgeable, and always arrives on time. The whole process from finding her to booking was easier than I expected.",
    },
  ];

  const HOW_STEPS = [
    {
      num: "01", icon: "Search", title: "Search your location and choose a service",
      desc: "Enter your neighbourhood or city. Filter by service type -- personal training, yoga, group classes, and more. You see only verified trainers available in your area with real pricing shown upfront.",
    },
    {
      num: "02", icon: "Users", title: "Browse trainer profiles and reviews",
      desc: "Every trainer's profile shows their certifications, experience, services offered, and reviews from verified clients. Read recent reviews, check their response rate, and message them directly if you have questions before booking.",
    },
    {
      num: "03", icon: "Calendar", title: "Book a session that fits your schedule",
      desc: "Pick a date and time from the trainer's live availability. Add your address for home sessions or confirm a meeting point for outdoor or facility-based sessions. You get an instant confirmation and a reminder before the session.",
    },
    {
      num: "04", icon: "Wallet", title: "Pay securely and confirm when done",
      desc: "Pay via M-Pesa through JiraniApp. Your payment is held securely and only released to the trainer after you confirm the session is complete. No cash, no awkward post-session payment conversations.",
    },
  ];

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <button onClick={() => navigate("/become-a-pro")} style={{
          display: "inline-flex", alignItems: "center", gap: 7,
          background: "none", border: `1px solid ${T.n200}`,
          color: T.n600, fontSize: 13, fontFamily: T.sans,
          padding: "8px 16px", borderRadius: 8, fontWeight: 500, cursor: "pointer",
          whiteSpace: "nowrap",
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.g400; e.currentTarget.style.color = T.g600; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.color = T.n600; }}>
          <Icon name="Dumbbell" size={13} strokeWidth={2} />
          Join as a fitness trainer
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(52px,7vw,96px) 20px clamp(44px,6vw,72px)", borderBottom: `1px solid ${T.n100}`, background: `linear-gradient(180deg, ${T.g50} 0%, ${T.white} 100%)` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>

          {/* Eyebrow */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 22 }}>
            <Icon name="Flame" size={14} color={T.g500} strokeWidth={2.2} />
            Fitness and Training
          </div>

          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(38px,7vw,68px)", fontWeight: 400, margin: "0 0 20px", lineHeight: 1.05, color: T.n900, letterSpacing: "-0.03em", maxWidth: 620 }}>
            Personal trainers and<br/>
            fitness classes, booked<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>in minutes</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.8, margin: "0 0 36px", maxWidth: 480 }}>
            Verified personal trainers, yoga instructors, and group fitness coaches across Kenya.
            Home sessions, outdoor training, and gym classes -- all bookable through JiraniApp with M-Pesa payment and the Jirani Guarantee.
          </p>

          {/* Search */}
          <div style={{ maxWidth: 560 }}>
            <LocationSearch onSearch={handleSearch} />
            <p style={{ fontSize: 12, color: T.n400, margin: "10px 0 0" }}>
              Available in Nairobi, Mombasa, Kisumu, Nakuru, Eldoret, Thika and surrounding areas
            </p>
          </div>
        </div>
      </div>

      {/* ══ TRUST STRIP ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
          {[
            { icon: "BadgeCheck", label: "All trainers ID-verified",      sub: "and certified for specialist services" },
            { icon: "Star",       label: "Real reviews only",             sub: "tied to completed bookings" },
            { icon: "Wallet",     label: "M-Pesa · payment held secure",  sub: "released after each session" },
          ].map(({ icon, label, sub }, i) => (
            <div key={i} style={{ padding: "20px 16px", display: "flex", alignItems: "flex-start", gap: 11, borderRight: i < 2 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name={icon} size={17} color={T.g500} strokeWidth={1.6} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n800, lineHeight: 1.3, marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 12, color: T.n400 }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px clamp(80px,10vw,120px)" }}>

        {/* ══ SERVICES GRID ══ */}
        <div style={{ paddingTop: "clamp(48px,7vw,64px)", marginBottom: "clamp(48px,7vw,64px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>What you can book</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Nine ways to train,<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>one place to book them</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            Prices shown are representative ranges based on jobs completed on the platform. Your final price depends on the trainer's rate, your location, and session length. All prices are shown before you confirm a booking.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
            {SERVICES.map((s, i) => <ServiceCard key={i} {...s} />)}
          </div>

          {/* Disclaimer */}
          <div style={{ marginTop: 20, padding: "14px 18px", borderRadius: 10, background: T.amberBg, border: `1px solid ${T.amberBorder}`, display: "flex", gap: 10 }}>
            <span style={{ fontSize: 14, flexShrink: 0 }}>ℹ</span>
            <p style={{ fontFamily: T.sans, fontSize: 13, color: "#92400e", margin: 0, lineHeight: 1.65 }}>
              Prices are per session before the JiraniApp platform commission (10-15%). Multi-week programme prices cover the full package. Exact rates are set by each trainer and visible on their profile before booking.
            </p>
          </div>
        </div>

        {/* ══ HOW IT WORKS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,64px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>How it works</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            From search to<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>first session in four steps</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 32px", lineHeight: 1.65 }}>
            Most clients complete their first booking in under five minutes.
          </p>
          <div style={{ paddingLeft: 4 }}>
            {HOW_STEPS.map((step, i) => (
              <HowStep key={i} {...step} isLast={i === HOW_STEPS.length - 1} />
            ))}
          </div>
          <div style={{ marginTop: 32, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={() => handleSearch("")} style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "13px 26px", borderRadius: 10, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em",
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Find a trainer near you
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={() => navigate("/how-it-works")} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "none", border: "none", cursor: "pointer",
              color: T.n500, fontSize: 13, fontFamily: T.sans, padding: 0,
            }}>
              Full platform guide <Icon name="ArrowRight" size={13} strokeWidth={2} color={T.n400} />
            </button>
          </div>
        </div>

        {/* ══ TRUST SECTION ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,64px)", padding: "clamp(28px,5vw,40px)", borderRadius: 18, background: T.g50, border: `1px solid ${T.g100}` }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Jirani Guarantee</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(22px,3.5vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Every trainer is verified.<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>Every session is protected.</em>
          </h2>
          <p style={{ fontFamily: T.sans, fontSize: 15, color: T.n600, lineHeight: 1.75, margin: "0 0 24px", maxWidth: 500 }}>
            We verify the identity of every fitness professional on the platform. For specialist services like pre/post-natal fitness and injury rehabilitation, we require proof of certification before those services are listed.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {[
              { icon: "BadgeCheck", title: "ID-verified professionals",  body: "Every trainer has submitted and passed identity verification before their profile goes live." },
              { icon: "Star",       title: "Genuine client reviews",      body: "Reviews are tied to real, completed bookings. Nothing is anonymous or unverified." },
              { icon: "ShieldCheck",title: "Dispute protection",          body: "If a session did not go as agreed, raise a dispute within 48 hours and our team reviews it fairly." },
              { icon: "Wallet",     title: "Secure M-Pesa payments",     body: "Your payment is held until you confirm the session is complete. No cash at the door." },
            ].map((item, i) => (
              <div key={i} style={{ padding: "18px", borderRadius: 12, background: T.white, border: `1px solid ${T.g100}`, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={item.icon} size={15} color={T.g500} strokeWidth={1.8} />
                </div>
                <div style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.g800 }}>{item.title}</div>
                <p style={{ fontFamily: T.sans, fontSize: 12, color: T.g700, lineHeight: 1.7, margin: 0 }}>{item.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══ TESTIMONIALS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,64px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Client experiences</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            What clients say<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>after their first month</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>
            From clients who found their trainer through JiraniApp.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            {TESTIMONIALS.map((t, i) => <Testimonial key={i} {...t} />)}
          </div>
        </div>

        {/* ══ FAQ ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,64px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Common questions</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Before you <em style={{ fontStyle: "italic", color: T.g600 }}>book a trainer</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            The questions new clients ask most often.
          </p>
          <div style={{ marginTop: 8 }}>
            {FAQS.map((f, i) => <FaqItem key={i} {...f} />)}
          </div>
        </div>

        {/* ══ PRO RECRUITMENT STRIP ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,64px)", padding: "24px 28px", borderRadius: 14, border: `1px solid ${T.n150}`, background: T.n50, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: T.white, border: `1px solid ${T.n200}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="Dumbbell" size={18} color={T.n600} strokeWidth={1.7} />
            </div>
            <div>
              <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 600, color: T.n900, marginBottom: 3 }}>Are you a fitness trainer or instructor?</div>
              <div style={{ fontFamily: T.sans, fontSize: 13, color: T.n400 }}>Join JiraniApp free. Set your own rates. Start getting booked.</div>
            </div>
          </div>
          <button onClick={() => navigate("/become-a-pro")} style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            background: T.n900, color: T.white, border: "none",
            padding: "11px 20px", borderRadius: 9, fontSize: 13, fontWeight: 600,
            fontFamily: T.sans, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
          }}
            onMouseEnter={e => e.currentTarget.style.background = T.n800}
            onMouseLeave={e => e.currentTarget.style.background = T.n900}>
            Join as a JiraniPro
            <Icon name="ArrowRight" size={13} color={T.white} strokeWidth={2} />
          </button>
        </div>

        {/* ══ FOOTER CTA ══ */}
        <div style={{ padding: "clamp(36px,5vw,52px)", borderRadius: 20, border: `1px solid ${T.g100}`, textAlign: "center", background: T.g50 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: T.white, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <Icon name="Zap" size={22} color={T.g500} strokeWidth={1.6} />
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Your trainer is<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>already available near you</em>
          </h2>
          <p style={{ fontFamily: T.sans, fontSize: 15, color: T.n400, margin: "0 auto 28px", lineHeight: 1.75, maxWidth: 380 }}>
            Search your area, browse verified trainers, and book your first session today.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => handleSearch("")} style={{
              padding: "14px 32px", background: T.g500, color: T.white,
              border: "none", borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Find a trainer
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={() => navigate("/how-it-works")} style={{
              padding: "14px 28px", background: "transparent", color: T.n700,
              border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              How JiraniApp works
            </button>
          </div>
          <p style={{ fontFamily: T.sans, fontSize: 12, color: T.n300, margin: "18px 0 0" }}>
            Verified trainers · M-Pesa payment · Jirani Guarantee on every session
          </p>
        </div>

      </div>
    </div>
  );
}